import React, { useRef, useState, useEffect, useCallback } from "react";
import {
    ArrowUp,
    Send,
    Plus,
    Crosshair,
    Sparkles,
    Table2,
    CircleStop,
    Loader2,
    ChevronRight,
    Upload,
    X,
    LayoutGrid,
    FileText,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useGoogleSheetsIntegration } from '../../documents/hooks/useGoogleSheetsIntegration';
import {
    isWorkspaceDragEvent,
    parseWorkspaceDragPayload,
} from '../../documents/utils/workspaceDrag';
import { getUserFriendlyErrorMessage } from '../../../utils/errorMessages';

export interface FocusedDocumentChip {
    id: string;
    name: string;
    kind?: 'google_sheet' | 'document';
}

interface SendMessageOptions {
    focusedDocuments?: FocusedDocumentChip[];
}

interface QuestionInputProps {
    onSendMessage: (message: string, options?: SendMessageOptions) => void;
    /** Rich chrome for /documents (Agent + tools, Finz Docs styling). */
    variant?: 'default' | 'documents' | 'signals';
    /** Refresh file list after Google Sheet import (Documents tab). */
    onDocumentsRefresh?: () => void;
    /** After Picker queues import jobs, e.g. navigate to Documents/Other/Google Sheets so new rows are visible. */
    onGoogleSheetsImportQueued?: () => void;
    /** Folder/category context for the second top chip (e.g. "All folders"). */
    documentsFolderLabel?: string;
    /** Documents CFO chat: show Stop and block sending while a request is in flight or activity shows in progress. */
    documentsAgentLoading?: boolean;
    /** Abort stream + cancel pending sheet proposals (idempotent on server). */
    onStopDocumentsAgent?: () => void | Promise<void>;
    contextLabel?: string;
    onClearContext?: () => void;
    contextOptions?: Array<{ id: string; label: string }>;
    onSelectContext?: (id: string) => void;
    /** Documents currently pinned to this chat turn (drag/drop chips). */
    focusedDocuments?: FocusedDocumentChip[];
    /** Replace the focused document chip list. */
    onFocusedDocumentsChange?: (docs: FocusedDocumentChip[]) => void;
    /** Resolve workspace drag payload file ids into display chips. */
    onResolveWorkspaceDragFiles?: (fileIds: string[]) => Promise<FocusedDocumentChip[]>;
    /** Optional upload handler for OS-level file drops. */
    onUploadDroppedFiles?: (files: File[]) => Promise<FocusedDocumentChip[]>;
}

const InputBox: React.FC<QuestionInputProps> = ({
    onSendMessage,
    variant = 'default',
    onDocumentsRefresh,
    onGoogleSheetsImportQueued,
    documentsFolderLabel = 'All folders',
    documentsAgentLoading = false,
    onStopDocumentsAgent,
    contextLabel,
    onClearContext,
    contextOptions = [],
    onSelectContext,
    focusedDocuments = [],
    onFocusedDocumentsChange,
    onResolveWorkspaceDragFiles,
    onUploadDroppedFiles,
}) => {
    const [input, setInput] = useState("");
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const dragDepthRef = useRef(0);
    const dropHandledRef = useRef(false);
    const [dragActive, setDragActive] = useState(false);

    const noopRefresh = useCallback(() => { }, []);
    const isDocs = variant === 'documents';
    const isSignals = variant === 'signals';
    const useRichChrome = isDocs || isSignals;
    const sheetsEnabled = isDocs && Boolean(onDocumentsRefresh);
    const sheets = useGoogleSheetsIntegration(onDocumentsRefresh ?? noopRefresh, {
        enabled: sheetsEnabled,
        onImportQueued: onGoogleSheetsImportQueued,
    });

    const addToolsRef = useRef<HTMLDivElement>(null);
    const toolChipRef = useRef<HTMLDivElement>(null);
    const [addToolsOpen, setAddToolsOpen] = useState(false);
    const [toolMenuOpen, setToolMenuOpen] = useState(false);
    const hasSignalContextOptions = isSignals && contextOptions.length > 0;

    useEffect(() => {
        if (!addToolsOpen && !toolMenuOpen) return;
        const close = (e: MouseEvent) => {
            const t = e.target as Node;
            if (addToolsRef.current?.contains(t)) return;
            if (toolChipRef.current?.contains(t)) return;
            setAddToolsOpen(false);
            setToolMenuOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [addToolsOpen, toolMenuOpen]);

    const [isVerified, setIsVerified] = useState(() => {
        return sessionStorage.getItem('approval_status') === 'true';
    });

    const handleSubmit = (e: React.FormEvent | React.KeyboardEvent) => {
        e.preventDefault();
        if (useRichChrome && documentsAgentLoading) return;
        if (input.trim()) {
            onSendMessage(input, {
                focusedDocuments: isDocs ? focusedDocuments : [],
            });
            setInput("");
        }
    };

    const removeFocusedDoc = (id: string) => {
        if (!onFocusedDocumentsChange) return;
        onFocusedDocumentsChange(focusedDocuments.filter((d) => d.id !== id));
    };

    const clearFocusedDocs = () => {
        if (!onFocusedDocumentsChange) return;
        onFocusedDocumentsChange([]);
    };

    const mergeFocusedDocs = useCallback(
        (incoming: FocusedDocumentChip[]) => {
            if (!onFocusedDocumentsChange || incoming.length === 0) return;
            const next = [...focusedDocuments];
            const seen = new Set(next.map((d) => d.id));
            for (const doc of incoming) {
                const id = String(doc.id || '').trim();
                const name = String(doc.name || '').trim();
                if (!id || !name || seen.has(id)) continue;
                seen.add(id);
                next.push({ id, name, kind: doc.kind || 'document' });
                if (next.length >= 8) break; // keep chips bounded for UI + payload size
            }
            onFocusedDocumentsChange(next);
        },
        [focusedDocuments, onFocusedDocumentsChange],
    );

    const handleDragEnter: React.DragEventHandler<HTMLDivElement> = (e) => {
        if (!isDocs) return;
        dragDepthRef.current += 1;
        if (dragDepthRef.current > 0) setDragActive(true);
        e.preventDefault();
    };

    const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
        if (!isDocs) return;
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setDragActive(false);
        e.preventDefault();
    };

    /** Allow drop anywhere in the composer (required on some browsers even when a child is the event target). */
    const handleDragOverCapture: React.DragEventHandler<HTMLDivElement> = (e) => {
        if (!isDocs) return;
        e.preventDefault();
        // ``copy`` is always allowed for our workspace source (``effectAllowed`` includes ``copy``).
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleTextAreaDragOver: React.DragEventHandler<HTMLTextAreaElement> = (e) => {
        if (!isDocs) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    /** Match Documents upload zone: PDF, images, spreadsheets, Word, text. */
    const isAcceptedOsDropFile = (f: File) => {
        const n = f.name.toLowerCase();
        return (
            n.endsWith('.pdf') ||
            n.endsWith('.csv') ||
            n.endsWith('.xlsx') ||
            n.endsWith('.xls') ||
            n.endsWith('.docx') ||
            n.endsWith('.txt') ||
            n.endsWith('.jpg') ||
            n.endsWith('.jpeg') ||
            n.endsWith('.png') ||
            n.endsWith('.webp') ||
            n.endsWith('.heic') ||
            n.endsWith('.heif')
        );
    };

    const handleFormDragOver: React.DragEventHandler<HTMLFormElement> = (e) => {
        if (!isDocs) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDrop: React.DragEventHandler<HTMLDivElement | HTMLTextAreaElement> = async (e) => {
        if (!isDocs) return;
        if (dropHandledRef.current) return;
        dropHandledRef.current = true;
        try {
            e.preventDefault();
            dragDepthRef.current = 0;
            setDragActive(false);
            if (documentsAgentLoading) {
                toast.error('Wait for the agent to finish, then drop your files.');
                return;
            }

            // Parse Finz workspace drag first — some browsers omit custom MIME types in ``types`` but still expose
            // ``getData('text/plain')`` (mirrored payload) on ``drop``.
            const workspacePayload = parseWorkspaceDragPayload(e);
            if (workspacePayload?.kind === 'files' && workspacePayload.fileIds?.length) {
                if (!onResolveWorkspaceDragFiles) {
                    toast.error('Document drop is not available right now.');
                    return;
                }
                try {
                    const docs = await onResolveWorkspaceDragFiles(workspacePayload.fileIds.slice(0, 8));
                    mergeFocusedDocs(docs);
                    if (docs.length) {
                        toast.success(docs.length === 1 ? 'Added to chat focus' : `Added ${docs.length} files to focus`);
                    } else {
                        toast.error('Could not attach that file. Select a workspace and try again.');
                    }
                } catch (err: any) {
                    toast.error(getUserFriendlyErrorMessage(err, 'Failed to attach dropped documents.'));
                }
                return;
            }
            if (workspacePayload?.kind === 'folder') {
                toast.error('Folders cannot be pinned to chat — drop a file row instead.');
                return;
            }
            if (isWorkspaceDragEvent(e)) {
                // Custom MIME advertised but payload missing (rare); helps support debug the next line in devtools.
                // eslint-disable-next-line no-console
                console.warn('[InputBox] workspace drag types without payload', Array.from(e.dataTransfer?.types || []));
                toast.error('Could not read that Finz drag payload. Try again or use Focus from the file list.');
                return;
            }

            const files = Array.from(e.dataTransfer.files || []);
            const types = Array.from(e.dataTransfer?.types || []);
            const hintedOsFiles = types.includes('Files');
            if (!files.length) {
                if (hintedOsFiles) {
                    toast.error(
                        'Drop did not include file data. Try the upload zone above, or drop from Finder / Explorer (not from another browser tab).',
                    );
                }
                return;
            }
            if (!onUploadDroppedFiles) {
                toast.error('Drag-and-drop uploads are not available in this view.');
                return;
            }
            const accepted = files.filter(isAcceptedOsDropFile);
            if (!accepted.length) {
                toast.error('Use PDF, CSV, Excel, images (JPG/PNG/HEIC), DOCX, or TXT.');
                return;
            }
            try {
                const docs = await onUploadDroppedFiles(accepted.slice(0, 5));
                mergeFocusedDocs(docs);
                if (docs.length) {
                    toast.success(docs.length === 1 ? 'File attached' : `${docs.length} files attached`);
                } else {
                    toast.error('Upload finished but no file was attached. Try again or use Select Files.');
                }
            } catch (err: any) {
                toast.error(getUserFriendlyErrorMessage(err, 'Upload failed for dropped files.'));
            }
        } finally {
            window.setTimeout(() => {
                dropHandledRef.current = false;
            }, 0);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    useEffect(() => {
        if (textAreaRef.current) {
            const maxHeight = 200;

            textAreaRef.current.style.height = "auto";
            const newHeight = Math.min(textAreaRef.current.scrollHeight, maxHeight);
            textAreaRef.current.style.height = `${newHeight}px`;
            textAreaRef.current.style.overflowY =
                textAreaRef.current.scrollHeight > maxHeight ? "auto" : "hidden";
        }
    }, [input]);

    const openAddTools = () => {
        setToolMenuOpen(false);
        setAddToolsOpen((o) => !o);
    };

    const openToolMenu = () => {
        setAddToolsOpen(false);
        setToolMenuOpen((o) => !o);
    };

    const onRemoveTool = () => {
        if (!window.confirm('Disconnect Google and remove Sheets from this agent?')) return;
        setToolMenuOpen(false);
        void sheets.handleDisconnect();
    };

    return (
        <form onSubmit={handleSubmit} onDragOver={handleFormDragOver}>
            <div className="space-y-2">
                <div
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOverCapture={handleDragOverCapture}
                    onDrop={handleDrop}
                    className={
                        isDocs
                            ? `max-h-[320px] relative rounded-2xl border border-gray-200 bg-white p-3 shadow-sm flex flex-col gap-2`
                            : isSignals
                                ? `min-h-[148px] max-h-[360px] relative rounded-2xl border border-gray-200 bg-white px-4 pt-4 pb-3 shadow-sm flex flex-col gap-3`
                                : `max-h-[300px] relative border rounded-xl p-4 shadow-sm bg-stone-50`
                    }
                >
                    {!isVerified && !useRichChrome && (
                        <div className="absolute inset-0 bg-white/60 z-[1000] cursor-not-allowed rounded-2xl" />
                    )}

                    {(useRichChrome || contextLabel) && (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-800">
                                <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                Agent
                            </span>
                            <span
                                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700"
                                title="Document context"
                            >
                                <Crosshair className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden />
                                {documentsFolderLabel}
                            </span>
                        </div>
                    )}

                    {isDocs && focusedDocuments.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1.5">
                            <span className="text-[11px] font-medium text-slate-600">Focus</span>
                            {focusedDocuments.map((doc) => (
                                <span
                                    key={doc.id}
                                    className="inline-flex max-w-[220px] items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-700"
                                    title={doc.name}
                                >
                                    {doc.kind === 'google_sheet' ? (
                                        <Table2 className="h-3 w-3 shrink-0 text-emerald-600" aria-hidden />
                                    ) : (
                                        <FileText className="h-3 w-3 shrink-0 text-slate-500" aria-hidden />
                                    )}
                                    <span className="truncate">{doc.name}</span>
                                    <button
                                        type="button"
                                        className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                        onClick={() => removeFocusedDoc(doc.id)}
                                        aria-label={`Remove ${doc.name} from focus`}
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </span>
                            ))}
                            <button
                                type="button"
                                className="ml-auto text-[11px] font-medium text-slate-500 hover:text-slate-700"
                                onClick={clearFocusedDocs}
                            >
                                Clear
                            </button>
                        </div>
                    )}

                    {isDocs && dragActive && (
                        <div
                            className="pointer-events-none absolute inset-0 z-20 rounded-2xl bg-white/70 ring-1 ring-inset ring-gray-200/90"
                            aria-hidden
                        >
                            <div className="flex h-full items-center justify-center px-3">
                                <span className="text-center text-[11px] font-medium tracking-tight text-gray-500">
                                    Drop to attach
                                </span>
                            </div>
                        </div>
                    )}

                    <textarea
                        ref={textAreaRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onDragOver={isDocs ? handleTextAreaDragOver : undefined}
                        placeholder={isDocs ? 'Ask a question' : 'Type a message'}
                        className={
                            isDocs
                                ? `${sheetsEnabled ? 'min-h-[52px]' : 'min-h-[28px] pr-12'} text-gray-800 bg-transparent border-none outline-none resize-none w-full overflow-y-auto max-h-[200px] text-sm placeholder:text-gray-400 placeholder:italic`
                                : isSignals
                                    ? `min-h-[56px] pr-14 pt-1 text-gray-800 bg-transparent border-none outline-none resize-none w-full overflow-y-auto max-h-[200px] text-sm placeholder:text-gray-400 placeholder:italic`
                                    : 'pr-6 min-h-[24px] text-zinc-600 bg-transparent border-none outline-none resize-none w-full overflow-y-auto max-h-[200px]'
                        }
                        rows={isDocs ? (sheetsEnabled ? 2 : 1) : isSignals ? 2 : 1}
                    />

                    {isDocs && sheetsEnabled ? (
                        <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-2.5 mt-0.5">
                            <div className="flex min-w-0 flex-1 items-center gap-1">
                                <div className="relative shrink-0" ref={addToolsRef}>
                                    <button
                                        type="button"
                                        onClick={openAddTools}
                                        className="rounded-lg p-1.5 text-gray-600 transition hover:bg-gray-100"
                                        aria-expanded={addToolsOpen}
                                        aria-label="Add tools"
                                    >
                                        <Plus className="h-4 w-4" />
                                    </button>
                                    {addToolsOpen && (
                                        <div
                                            role="menu"
                                            className="absolute bottom-full left-0 z-[1100] mb-1 w-[min(100vw-2rem,280px)] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                                        >
                                            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2.5">
                                                <div className="flex min-w-0 items-center gap-2">
                                                    <Table2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                                                    <span className="truncate text-sm font-medium text-gray-800">
                                                        Google Sheets
                                                    </span>
                                                </div>
                                                {!sheets.status?.connected ? (
                                                    <button
                                                        type="button"
                                                        className="shrink-0 text-sm font-medium text-sky-600 hover:underline"
                                                        onClick={() => {
                                                            setAddToolsOpen(false);
                                                            void sheets.handleConnect();
                                                        }}
                                                    >
                                                        Connect
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="shrink-0 text-sm font-medium text-sky-600 hover:underline disabled:opacity-50"
                                                        disabled={sheets.loading}
                                                        onClick={() => {
                                                            setAddToolsOpen(false);
                                                            void sheets.handleImport();
                                                        }}
                                                    >
                                                        Import
                                                    </button>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                role="menuitem"
                                                className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                                                onClick={() => {
                                                    setAddToolsOpen(false);
                                                    toast('More integrations are coming soon.');
                                                }}
                                            >
                                                <span className="flex items-center gap-2">
                                                    <LayoutGrid className="h-4 w-4 text-gray-500" aria-hidden />
                                                    More apps
                                                </span>
                                                <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {useRichChrome && documentsAgentLoading && onStopDocumentsAgent ? (
                                    <button
                                        type="button"
                                        className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-800 transition hover:bg-red-100"
                                        title="Stop stream and cancel pending sheet edit proposals"
                                        aria-label="Stop agent"
                                        onClick={() => void onStopDocumentsAgent()}
                                    >
                                        <CircleStop className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                        Stop
                                    </button>
                                ) : null}

                                <div className="relative min-w-0" ref={toolChipRef}>
                                    <button
                                        type="button"
                                        onClick={openToolMenu}
                                        className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-800 shadow-sm transition hover:bg-gray-50"
                                        aria-expanded={toolMenuOpen}
                                        aria-haspopup="menu"
                                        title="Google Sheets tool"
                                    >
                                        <Table2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
                                        <span className="truncate">Google Sheets</span>
                                    </button>
                                    {toolMenuOpen && (
                                        <div
                                            role="menu"
                                            className="absolute bottom-full left-0 z-[1100] mb-1 min-w-[200px] rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                                        >
                                            <button
                                                type="button"
                                                role="menuitem"
                                                disabled={sheets.loading}
                                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                                                onClick={() => {
                                                    setToolMenuOpen(false);
                                                    if (sheets.status?.connected) {
                                                        void sheets.handleImport();
                                                    } else {
                                                        void sheets.handleConnect();
                                                    }
                                                }}
                                            >
                                                {sheets.loading ? (
                                                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                                                ) : (
                                                    <Upload className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                                                )}
                                                Upload more files
                                            </button>
                                            <button
                                                type="button"
                                                role="menuitem"
                                                disabled={sheets.loading || !sheets.status?.connected}
                                                title={
                                                    sheets.status?.connected
                                                        ? undefined
                                                        : 'Connect Google first (use + or Upload more files)'
                                                }
                                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                                                onClick={onRemoveTool}
                                            >
                                                <X className="h-4 w-4 shrink-0" aria-hidden />
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={`${input && !(useRichChrome && documentsAgentLoading) ? 'bg-gray-900' : 'bg-gray-300 cursor-not-allowed'} flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition-colors`}
                                disabled={!input.trim() || (useRichChrome && documentsAgentLoading)}
                                aria-label="Send message"
                            >
                                <ArrowUp className="h-4 w-4" />
                            </button>
                        </div>
                    ) : hasSignalContextOptions ? (
                        <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-2.5 mt-0.5">
                            <div className="flex min-w-0 flex-1 items-center gap-1">
                                <div className="relative shrink-0" ref={addToolsRef}>
                                    <button
                                        type="button"
                                        onClick={openAddTools}
                                        className="rounded-lg p-1.5 text-gray-600 transition hover:bg-gray-100"
                                        aria-expanded={addToolsOpen}
                                        aria-label="Add focus signal"
                                    >
                                        <Plus className="h-4 w-4" />
                                    </button>
                                    {addToolsOpen && (
                                        <div
                                            role="menu"
                                            className="absolute bottom-full left-0 z-[1100] mb-1 max-h-[280px] w-[min(100vw-2rem,320px)] overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                                        >
                                            <div className="border-b border-gray-100 px-3 py-2.5 text-sm font-medium text-gray-800">
                                                Focus signal
                                            </div>
                                            {contextOptions.map((option) => (
                                                <button
                                                    key={option.id}
                                                    type="button"
                                                    role="menuitem"
                                                    className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                                                    onClick={() => {
                                                        setAddToolsOpen(false);
                                                        onSelectContext?.(option.id);
                                                    }}
                                                >
                                                    <span className="truncate">{option.label}</span>
                                                    {contextLabel === option.label ? (
                                                        <span className="shrink-0 text-xs font-medium text-indigo-600">Focused</span>
                                                    ) : null}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={`${input && !(useRichChrome && documentsAgentLoading) ? 'bg-gray-900' : 'bg-gray-300 cursor-not-allowed'} flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition-colors`}
                                disabled={!input.trim() || (useRichChrome && documentsAgentLoading)}
                                aria-label="Send message"
                            >
                                <ArrowUp className="h-4 w-4" />
                            </button>
                        </div>
                    ) : (
                        <div className={`absolute ${isSignals ? 'bottom-4 right-4' : useRichChrome ? 'bottom-3 right-3' : 'bottom-4 right-4'}`}>
                            <button
                                type="submit"
                                className={`${input && !(useRichChrome && documentsAgentLoading) ? 'bg-gray-900' : 'bg-gray-300 cursor-not-allowed'} w-9 h-9 flex items-center justify-center text-white rounded-full transition-colors`}
                                disabled={!input.trim() || (useRichChrome && documentsAgentLoading)}
                                aria-label="Send message"
                            >
                                {useRichChrome ? <ArrowUp className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                            </button>
                        </div>
                    )}
                </div>

                <div className="text-center font-inter text-[10px] font-normal leading-[100%] tracking-[0px] text-[#989DA3]">
                    {useRichChrome
                        ? 'AI CFO can make mistakes. Verify before acting.'
                        : 'AI CFO can make mistakes. Please double-check responses.'}
                </div>
            </div>
        </form>
    );
};

export default InputBox;
